import * as THREE from 'three';

// Comment out all existing code for testing
/*
export const mapConfig = {
    islands: [
        { x: -800, z: 600, size: 1000, height: 250, treeCount: 40, hasVolcano: true },
        { x: 800, z: 600, size: 900, height: 230, treeCount: 35, hasVolcano: false },
        { x: -700, z: -500, size: 960, height: 260, treeCount: 38, hasVolcano: false },
        { x: 900, z: -400, size: 1040, height: 270, treeCount: 45, hasVolcano: false }
    ]
};

export function createMap(scene) {
    // ... existing code ...
}

function createLargeIslands(scene) {
    // ... existing code ...
}

function createDetailedIsland(size, x, z, maxHeight) {
    // ... existing code ...
}

function createVolcano(scene, x, y, z) {
    // ... existing code ...
}

function createTreeAtPosition(scene, island, x, z) {
    // ... existing code ...
}

function generateDeterministicTreePositions(radius, treeCount) {
    // ... existing code ...
}

function addRiverWithShore(scene) {
    // ... existing code ...
}
*/

// Test code for hill generation
export const mapConfig = {
    // Define larger continents instead of individual islands
    continents: [
        { x: -500, z: 500, size: 800, height: 250, treeCount: 100, hasVolcano: true },  // Northwest continent
        { x: 500, z: -400, size: 700, height: 250, treeCount: 80, hasVolcano: true },   // Southeast continent
        { x: -400, z: -500, size: 600, height: 250, treeCount: 90, hasVolcano: true }   // Southwest continent
    ],
    // Additional volcano locations (not part of main continents)
    additionalVolcanoes: [
        { x: 400, z: 400, height: 250 }  // Northeast volcano
    ]
};

export function createMap(scene) {
    createContinents(scene);
}

function createContinents(scene) {
    // Create main continents
    mapConfig.continents.forEach(continent => {
        createDetailedContinent(scene, continent.size, continent.x, continent.z, continent.height, continent.hasVolcano);

        // Add trees after the continent is created
        if (continent.treeCount > 0) {
            const treePositions = generateDeterministicTreePositions(continent.size * 0.4, continent.treeCount);
            treePositions.forEach(pos => {
                createTreeAtPosition(scene, continent, continent.x + pos.x, continent.z + pos.z);
            });
        }
    });

    // Create additional volcanoes
    mapConfig.additionalVolcanoes.forEach(volcano => {
        createVolcano(scene, volcano.x, volcano.height * 0.1, volcano.z, volcano.height);
    });
}

function createDetailedContinent(scene, size, x, z, maxHeight, hasVolcano) {
    // Create a plane geometry for the continent
    const segments = 128; // Increased for more detailed terrain
    const geometry = new THREE.PlaneGeometry(size, size, segments, segments);

    // Modify the vertices to create the continent shape
    const vertices = geometry.attributes.position.array;
    for (let i = 0; i < vertices.length; i += 3) {
        const vx = vertices[i];
        const vz = vertices[i + 1];

        // Calculate distance from center for radial falloff
        const dx = vx / (size / 2);
        const dz = vz / (size / 2);
        const distanceSquared = dx * dx + dz * dz;

        // Create varied terrain height
        const baseTerrainHeight = 20; // Base height for all terrain
        const hillHeight = 40; // Height for larger hills

        // Generate different types of noise for varied terrain
        const terrainNoise = Math.cos(vx * 0.01) * Math.sin(vz * 0.01) * 15; // Large terrain variations
        const hillNoise = Math.cos(vx * 0.03) * Math.sin(vz * 0.03) * hillHeight; // Medium hills
        const detailNoise = (Math.cos(vx * 0.1) + Math.sin(vz * 0.1)) * 5; // Small details

        // Create more natural shore transition with noise
        const shoreNoise = (Math.sin(vx * 0.05) + Math.cos(vz * 0.05)) * 0.1; // Add noise to shore boundary
        const beachTransition = Math.max(0, 1 - distanceSquared * 1.2 + shoreNoise);
        const isBeach = beachTransition < 0.2;
        const isShore = beachTransition < 0.3 && beachTransition >= 0.2;

        // Combine different height components
        let height;
        if (isBeach) {
            // Beach area - very gradual slope up from water
            height = baseTerrainHeight * beachTransition * 0.3 + detailNoise * 0.2;
        } else if (isShore) {
            // Shore area - transition between beach and terrain
            const shoreProgress = (beachTransition - 0.2) / 0.1; // 0 at beach, 1 at terrain
            height = baseTerrainHeight * 0.3 + (detailNoise * 0.3 + terrainNoise * 0.2) * shoreProgress;
        } else {
            // Main terrain with hills
            height = baseTerrainHeight + terrainNoise + hillNoise * beachTransition + detailNoise;
        }

        vertices[i + 2] = Math.max(0, height); // Ensure height is never negative
    }

    // Update the geometry
    geometry.attributes.position.needsUpdate = true;
    geometry.computeVertexNormals();

    // Create materials for different terrain types
    const terrainMaterial = new THREE.MeshPhongMaterial({
        vertexColors: true,
        flatShading: true,
        shininess: 5
    });

    // Add vertex colors for terrain variation
    const colors = new Float32Array(vertices.length);
    const color = new THREE.Color();

    for (let i = 0; i < vertices.length; i += 3) {
        const vx = vertices[i];
        const vz = vertices[i + 1];
        const height = vertices[i + 2];

        // Calculate beach transition again for coloring
        const dx = vx / (size / 2);
        const dz = vz / (size / 2);
        const distanceSquared = dx * dx + dz * dz;
        const shoreNoise = (Math.sin(vx * 0.05) + Math.cos(vz * 0.05)) * 0.1;
        const beachTransition = Math.max(0, 1 - distanceSquared * 1.2 + shoreNoise);

        if (beachTransition < 0.2) {
            // Beach color (sandy)
            color.setHex(0xdeb887);
        } else if (beachTransition < 0.3) {
            // Shore transition color (mix between beach and grass)
            const mix = (beachTransition - 0.2) / 0.1;
            color.setHex(0xdeb887).lerp(new THREE.Color(0x5a8054), mix);
        } else {
            // Main terrain color (grass with height variation)
            const heightFactor = Math.min(1, height / 40);
            color.setHex(0x5a8054).lerp(new THREE.Color(0x3d5e3a), heightFactor);
        }

        colors[i] = color.r;
        colors[i + 1] = color.g;
        colors[i + 2] = color.b;
    }

    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    // Create the mesh and add it to the scene
    const continent = new THREE.Mesh(geometry, terrainMaterial);
    continent.rotation.x = -Math.PI / 2;
    continent.position.set(x, 0, z);

    scene.add(continent);

    // Add volcano if specified
    if (hasVolcano) {
        createVolcano(scene, x, maxHeight * 0.1, z, maxHeight);
    }

    return continent;
}

function createVolcano(scene, x, y, z, islandHeight) {
    // Create the main volcano cone with wider base
    const volcanoGeometry = new THREE.ConeGeometry(100, 150, 32); // Increased base radius from 80 to 100
    const volcanoMaterial = new THREE.MeshPhongMaterial({
        color: 0x808080,  // Grey color for volcano
        flatShading: true,
        shininess: 10  // Added more shininess for rock-like appearance
    });

    const volcano = new THREE.Mesh(volcanoGeometry, volcanoMaterial);
    volcano.position.set(x, y, z);
    scene.add(volcano);

    // Create a wider base to blend with the island
    const baseGeometry = new THREE.ConeGeometry(160, 80, 32); // Increased from 120 to 160 for wider base
    const baseMaterial = new THREE.MeshPhongMaterial({
        color: 0x696969,  // Slightly darker grey for base
        flatShading: true,
        shininess: 5
    });

    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    base.position.set(x, y * 0.3, z);  // Lowered from 0.5 to 0.3 to reduce gap
    scene.add(base);

    // Add lava glow at the top
    const lavaGeometry = new THREE.ConeGeometry(25, 35, 32); // Increased size slightly
    const lavaMaterial = new THREE.MeshPhongMaterial({
        color: 0xff4400,
        emissive: 0xff0000,
        emissiveIntensity: 0.5,  // Added emissive intensity
        flatShading: true
    });

    const lava = new THREE.Mesh(lavaGeometry, lavaMaterial);
    lava.position.set(x, y + 75, z); // Adjusted height to match new volcano size
    scene.add(lava);
}

function createTreeAtPosition(scene, island, x, z) {
    const treeGroup = new THREE.Group();

    // Trunk with lighter brown
    const trunkGeometry = new THREE.CylinderGeometry(2, 3, 20, 8);
    const trunkMaterial = new THREE.MeshPhongMaterial({
        color: 0x8B6B42,  // Lighter brown for trunk
        shininess: 5
    });
    const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
    trunk.position.y = 10;

    // Leaves with lighter green
    const leavesGeometry = new THREE.ConeGeometry(8, 25, 8);
    const leavesMaterial = new THREE.MeshPhongMaterial({
        color: 0x2F8F3F,  // Brighter green for leaves
        shininess: 10
    });
    const leaves = new THREE.Mesh(leavesGeometry, leavesMaterial);
    leaves.position.y = 25;

    treeGroup.add(trunk);
    treeGroup.add(leaves);
    treeGroup.position.set(x, 0, z);

    scene.add(treeGroup);
}

function generateDeterministicTreePositions(radius, treeCount) {
    const positions = [];
    const goldenAngle = Math.PI * (3 - Math.sqrt(5)); // Golden angle for better distribution

    for (let i = 0; i < treeCount; i++) {
        const distance = Math.sqrt(i / treeCount) * radius;
        const angle = i * goldenAngle;

        const x = Math.cos(angle) * distance;
        const z = Math.sin(angle) * distance;

        positions.push({ x, z });
    }

    return positions;
}